import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Loader from "../../components/Loader/Loader";
import { getAdminProfile } from "../../services/authService";
import {
  updatePassword,
  updatePin,
  verifyUpdateAuthValueOtp,
} from "../../services/ProfileService/profileService";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import OtpModal from "../Components/OtpModal";

import { ShieldCheck, CircleDot } from "lucide-react";

export default function Profile() {
  const [mobile, setMobile] = useState("");
  const [updateType, setUpdateType] = useState("password");
  const [oldValue, setOldValue] = useState("");
  const [newValue, setNewValue] = useState("");
  const [confirmValue, setConfirmValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpIdentifier, setOtpIdentifier] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    const fetchMobile = async () => {
      try {
        const response = await getAdminProfile();
        const mobileNumber = response?.data?.mobile_number;
        setMobile(mobileNumber);
      } catch (error) {
        console.error("Error fetching mobile number", error);
      }
    };

    fetchMobile();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newValue !== confirmValue) {
      showErrorToast(`New ${updateType} and confirmation don't match`);
      return;
    }

    try {
      setLoading(true);
      let response;
      let payload;

      if (updateType === "password") {
        payload = {
          current_password: oldValue,
          new_password: newValue,
          new_password_confirmation: confirmValue,
        };
        response = await updatePassword(payload);
      } else {
        payload = {
          current_pin: oldValue,
          new_pin: newValue,
          new_pin_confirmation: confirmValue,
        };
        response = await updatePin(payload);
      }

      // If OTP is required, show modal
      if (response.data?.identifier) {
        setOtpIdentifier(response.data.identifier);
        setShowOtpModal(true);
        showSuccessToast(
          response.data?.message ||
            `OTP has been sent to your mobile number to update your ${updateType}.`,
        );
        return;
      }

      showSuccessToast(`${updateType} updated successfully!`);
      resetForm();
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerification = async (otp) => {
    try {
      setIsVerifying(true);

      const response = await verifyUpdateAuthValueOtp({
        identifier: otpIdentifier,
        otp,
      });

      // Check for successful response
      if (
        response?.status === 200 ||
        response?.data?.status?.code === 200 ||
        response?.status?.code === 200
      ) {
        showSuccessToast(`${capitalize(updateType)} updated successfully!`);

        // Close modal and reset form after a short delay
        setTimeout(() => {
          setShowOtpModal(false);
          setIsVerifying(false);
          resetForm();
        }, 500);

        return; // Exit early to prevent finally block from resetting isVerifying
      } else {
        const errorMsg =
          response?.data?.error || response?.data?.message || "Invalid OTP.";
        showErrorToast(errorMsg);
      }
    } catch (err) {
      const errorMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Invalid or expired OTP.";
      showErrorToast(errorMsg);
    } finally {
      // Only reset verifying state if we didn't succeed
      if (isVerifying) {
        setIsVerifying(false);
      }
    }
  };

  const handleModalClose = () => {
    setShowOtpModal(false);
    setOtpIdentifier("");
    setIsVerifying(false);
  };

  const resetForm = () => {
    setOldValue("");
    setNewValue("");
    setConfirmValue("");
    setOtpIdentifier("");
  };

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.2 }}
    >
      {loading && <Loader />}

      <div className="max-w-md w-5/6 mx-auto md:p-6 p-2 bg-white rounded-lg shadow-md mt-24">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Change {capitalize(updateType)}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex justify-center mb-4 bg-gray-100 rounded-lg p-1 border border-red-600">
            {["password", "pin"].map((type) => (
              <button
                key={type}
                type="button"
                className={`w-1/2 px-3 py-1 text-md font-medium rounded-lg transition ${
                  updateType === type
                    ? "bg-green-600 text-white"
                    : "bg-transparent text-gray-700"
                }`}
                onClick={() => setUpdateType(type)}
              >
                {capitalize(type)}
              </button>
            ))}
          </div>

          <div className="space-y-3">
            {["Old", "New", "Confirm New"].map((label, index) => {
              const value = [oldValue, newValue, confirmValue][index];
              const setValue = [setOldValue, setNewValue, setConfirmValue][
                index
              ];

              return (
                <input
                  key={label}
                  type={updateType === "password" ? "password" : "text"}
                  className="w-full px-4 py-2 border border-blue-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition"
                  placeholder={`${label} ${capitalize(updateType)}`}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  required
                  minLength={updateType === "password" ? 6 : 4}
                  maxLength={updateType === "password" ? 20 : 6}
                />
              );
            })}
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full px-4 py-2 text-white font-medium rounded-lg shadow-md transition ${
              loading
                ? "bg-blue-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Updating...
              </span>
            ) : (
              "Update"
            )}
          </button>
        </form>

        <PasswordStrengthTip />
      </div>

      <OtpModal
        mobile={mobile}
        isOpen={showOtpModal}
        onClose={handleModalClose}
        onVerify={handleOtpVerification}
        isLoading={isVerifying}
        title={`Enter OTP to update ${capitalize(updateType)}`}
        description={
          mobile
            ? `OTP sent to ****${mobile?.slice(-4)}`
            : "OTP sent to your registered mobile"
        }
      />
    </motion.div>
  );
}

const PasswordStrengthTip = () => {
  const tips = [
    "At least 8 characters long",
    "Mix uppercase & lowercase letters",
    "Include at least one number (0-9)",
    "Use a special character (e.g., @, #, $)",
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-md bg-green-100 border border-green-200/60 rounded-2xl p-5"
    >
      <div className="flex items-start gap-4">
        {/* Icon with Glow */}
        <div className="flex-shrink-0 p-2 bg-green-100 text-green-600 rounded-xl shadow-sm shadow-green-200/50">
          <ShieldCheck size={20} />
        </div>

        <div>
          <h4 className="text-lg font-bold text-green-900 mb-1">
            Make it Unbreakable
          </h4>
          <p className="text-[16px] text-green-700/80 mb-3 leading-normal">
            A strong password protects your account better. Try to follow these
            simple rules:
          </p>

          <ul className="space-y-2">
            {tips.map((tip, index) => (
              <li
                key={index}
                className="flex items-center gap-2 text-[14px] font-medium text-green-800/70"
              >
                <CircleDot size={10} className="text-green-400" />
                {tip}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
};
